import { useState } from 'react';
import {
  CYCLE_TIME_SUMMARY_METRICS,
  type CycleTimeSummaryMetricId,
} from './cycleTimeSummaryMetrics';
import { useCycleTimeQuery, type MetricsCardFetchScope } from './metricsCardQueries';

type CycleTimeSummaryMetricCardProps = {
  metric: CycleTimeSummaryMetricId;
  fetchScope?: MetricsCardFetchScope;
  cycleTimeConfig?: unknown;
  className?: string;
};

export default function CycleTimeSummaryMetricCard({
  metric,
  fetchScope,
  cycleTimeConfig,
  className = '',
}: CycleTimeSummaryMetricCardProps) {
  const config = CYCLE_TIME_SUMMARY_METRICS[metric];
  const Icon = config.icon;
  const [windowDays, setWindowDays] = useState(30);

  const cycleQuery = useCycleTimeQuery(fetchScope, windowDays, { cycleTimeConfig });
  const summary = cycleQuery.data?.cycleTime?.summary;
  const loading = cycleQuery.isPending;
  const value = config.getValue(summary);
  const showEmpty =
    !loading && metric !== 'prsMergedWithoutReview' && value === 0;

  const windowSelect = fetchScope?.orgId ? (
    <>
      <label htmlFor={config.selectId} className="sr-only">
        {config.selectLabel}
      </label>
      <select
        id={config.selectId}
        value={String(windowDays)}
        onChange={(e) => setWindowDays(Number(e.target.value))}
        className="select-mdl py-2 min-w-[120px] text-sm"
        aria-label={config.selectLabel}
      >
        <option value="7">7 days</option>
        <option value="30">30 days</option>
        <option value="365">365 days</option>
      </select>
    </>
  ) : null;

  const rootClass = ['mt-10', className].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <div className="card-mdl-static overflow-hidden h-full flex flex-col">
        <div className="flex flex-nowrap items-center gap-3 px-5 pt-5 pb-3 w-full">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Icon className="w-5 h-5 shrink-0 text-vivid-red" aria-hidden />
            <h3 className="truncate text-base font-medium text-slate-100">{config.title}</h3>
          </div>
          {windowSelect ? <div className="ml-auto shrink-0">{windowSelect}</div> : null}
        </div>

        <div className="px-5 pb-5 flex-1 flex flex-col justify-center">
          {loading ? (
            <div className="animate-pulse space-y-2" aria-hidden>
              <div className="h-9 w-16 rounded bg-dark-elevated" />
            </div>
          ) : showEmpty ? (
            <p className="text-sm text-slate-400">{config.emptyHint}</p>
          ) : (
            <p className="text-3xl font-bold tabular-nums text-slate-100">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
}

type CycleTimeSummaryMetricCardsProps = {
  fetchScope?: MetricsCardFetchScope;
  cycleTimeConfig?: unknown;
  className?: string;
};

export function CycleTimeSummaryMetricCards({
  fetchScope,
  cycleTimeConfig,
  className = '',
}: CycleTimeSummaryMetricCardsProps) {
  return (
    <div className={`card-grid-3-md-mdl gap-4 ${className}`.trim()}>
      <CycleTimeSummaryMetricCard
        metric="prsMergedIntoQa"
        fetchScope={fetchScope}
        cycleTimeConfig={cycleTimeConfig}
        className="!mt-0"
      />
      <CycleTimeSummaryMetricCard
        metric="prsMergedWithoutReview"
        fetchScope={fetchScope}
        cycleTimeConfig={cycleTimeConfig}
        className="!mt-0"
      />
      <CycleTimeSummaryMetricCard
        metric="deployCadenceIntervals"
        fetchScope={fetchScope}
        cycleTimeConfig={cycleTimeConfig}
        className="!mt-0"
      />
    </div>
  );
}
